import { z } from 'zod';

/** Route segments of the line-item ZIP/IP CSV endpoints. */
export enum LineItemTargetingCsvKind {
  ZipCodes = 'zip-codes',
  IpAddresses = 'ip-addresses',
}

/** Backend upload limit (64 MiB); a deployment proxy may reject smaller files first. */
export const LINE_ITEM_TARGETING_CSV_MAX_BYTES = 64 * 1024 * 1024;

const csvKindCountFields = {
  [LineItemTargetingCsvKind.ZipCodes]: 'zipCodesCount',
  [LineItemTargetingCsvKind.IpAddresses]: 'ipAddressesCount',
} as const;

export const lineItemTargetingCsvImportSchema = z.looseObject({
  imported: z.number().int().nonnegative(),
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
});

export type LineItemTargetingCsvImportResult = z.infer<typeof lineItemTargetingCsvImportSchema>;

export function lineItemTargetingCsvFilename(lineItemId: string, kind: LineItemTargetingCsvKind) {
  return `line-item-${lineItemId}-${kind}.csv`;
}

/** Saved entry count; ZIP/IP arrays are never returned, so they are not a fallback. */
export function readLineItemTargetingCsvCount(
  targeting: Readonly<Record<string, unknown>> | null | undefined,
  kind: LineItemTargetingCsvKind,
) {
  const value = targeting?.[csvKindCountFields[kind]];
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

/** Patch only the imported collection's count on a saved line-item targeting summary. */
export function applyLineItemTargetingCsvImport<
  TLineItem extends { targeting?: object | null | undefined },
>(lineItem: TLineItem, kind: LineItemTargetingCsvKind, result: LineItemTargetingCsvImportResult) {
  return {
    ...lineItem,
    targeting: { ...lineItem.targeting, [csvKindCountFields[kind]]: result.imported },
  };
}
